// Release B-Fidelity-2a · Slice D frontend — Applicant Questionnaire.
// Route: /access-request/:token/questionnaire (public, token-authenticated)
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#12233F";
const CREAM = "#F5F1E8";
const GOLD = "#C6A15B";

function Field({ f, value, onChange }) {
  const testid = `qf-${f.id}`;
  const common = { "data-testid": testid, style: inputStyle };
  if (f.type === "textarea") {
    return <textarea {...common} rows={5} value={value || ""}
                     onChange={(e) => onChange(e.target.value)}
                     maxLength={f.max_length || 4000}
                     style={{ ...inputStyle, minHeight: 100, resize: "vertical" }} />;
  }
  if (f.type === "select") {
    return (
      <select {...common} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">— Choose —</option>
        {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (f.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
    return (
      <div data-testid={testid} style={{ display: "grid", gap: 6 }}>
        {(f.options || []).map((o) => (
          <label key={o.value} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, textTransform: "none", letterSpacing: 0, color: "#0f172a", fontFamily: "system-ui" }}>
            <input type="checkbox" data-testid={`${testid}-${o.value}`}
                    checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <label data-testid={testid} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, textTransform: "none", letterSpacing: 0, color: "#0f172a", fontFamily: "system-ui" }}>
        <input type="checkbox" data-testid={`${testid}-input`} checked={!!value}
                onChange={(e) => onChange(e.target.checked)} />
        <span>Yes</span>
      </label>
    );
  }
  if (f.type === "number") {
    return <input {...common} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
  }
  if (f.type === "date") {
    return <input {...common} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (f.type === "email") {
    return <input {...common} type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (f.type === "phone") {
    return <input {...common} type="tel" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <input {...common} type="text" value={value || ""} maxLength={f.max_length || 4000}
                 onChange={(e) => onChange(e.target.value)} />;
}

export default function AccessQuestionnaire() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, schema: null,
                                        applicant: null, message: null, submitted: false });
  const [answers, setAnswers] = useState({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [submittedAt, setSubmittedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/access-requests/questionnaire/${token}`);
        setState({ loading: false, error: null, schema: r.data.schema,
                    applicant: r.data.applicant, message: r.data.message_to_applicant,
                    submitted: !!r.data.submitted });
        setAnswers(r.data.draft_answers || {});
        setSubmittedAt(r.data.submitted_at || null);
      } catch (e) {
        const code = e?.response?.data?.detail?.code || "load_failed";
        setState({ loading: false, error: code, schema: null, applicant: null,
                    message: null, submitted: false });
      }
    })();
  }, [token]);

  const set = (fid) => (val) => setAnswers((a) => ({ ...a, [fid]: val }));

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const r = await axios.post(`${API}/public/access-requests/questionnaire/${token}/save-draft`,
                                  { answers });
      setSavedAt(r.data.draft_saved_at);
    } catch (e) {
      setValidationErrors([e?.response?.data?.detail?.code || "save_failed"]);
    } finally { setSavingDraft(false); }
  };

  const submit = async () => {
    setSubmitting(true); setValidationErrors([]);
    try {
      const r = await axios.post(`${API}/public/access-requests/questionnaire/${token}/submit`,
                                  { answers });
      setSubmittedAt(r.data.submitted_at);
      setState((s) => ({ ...s, submitted: true }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "questionnaire_validation_failed" && Array.isArray(d.errors)) {
        setValidationErrors(d.errors);
      } else {
        setValidationErrors([d?.message || d?.code || "submit_failed"]);
      }
    } finally { setSubmitting(false); }
  };

  if (state.loading) return <Wrapper><Panel><p>Loading your questionnaire…</p></Panel></Wrapper>;
  if (state.error) return (
    <Wrapper><Panel>
      <h1 data-testid="qf-error-title" style={h1Style}>This link cannot be used</h1>
      <p data-testid="qf-error-code">Reason: <code>{state.error}</code></p>
      <p style={{ marginTop: 12, fontSize: 13, color: "#475569" }}>
        Please contact Edwin's team if you believe this is an error.
      </p>
      <Link to="/" style={linkStyle}>← ERF Enterprises home</Link>
    </Panel></Wrapper>
  );

  if (state.submitted) return (
    <Wrapper><Panel>
      <h1 data-testid="qf-success-title" style={h1Style}>Thank you</h1>
      <p>Your questionnaire has been received{submittedAt ? ` on ${new Date(submittedAt).toLocaleString()}` : ""}.</p>
      <p style={{ marginTop: 12, fontSize: 13, color: "#475569" }}>
        Edwin's team will review your answers and contact you with next steps. This link cannot be reused.
      </p>
      <Link to="/" style={linkStyle}>← ERF Enterprises home</Link>
    </Panel></Wrapper>
  );

  const s = state.schema;
  return (
    <Wrapper>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 20px" }}>
        <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>
          <div style={eyebrowStyle}>ERF ConstructionOS · Portal Access</div>
          <h1 data-testid="qf-title" style={h1Style}>{s.title}</h1>
          {state.applicant?.full_legal_name && (
            <p style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>
              For: <b>{state.applicant.full_legal_name}</b>
            </p>
          )}
          {s.description && <p style={{ marginTop: 8, color: "#475569", fontSize: 14 }}>{s.description}</p>}
          {state.message && (
            <div data-testid="qf-owner-message" style={{ marginTop: 12, padding: 12, background: "white",
                    border: `1px solid ${GOLD}66`, borderRadius: 4, fontSize: 13, color: "#0f172a" }}>
              <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: GOLD }}>Message from Edwin's team</div>
              <div style={{ marginTop: 4 }}>{state.message}</div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); submit(); }} style={{ marginTop: 28, display: "grid", gap: 26 }}>
          {(s.sections || []).map((section) => (
            <section key={section.id} data-testid={`qf-section-${section.id}`}
                     style={{ background: "white", padding: 20, borderRadius: 4,
                              border: "1px solid #e2e8f0" }}>
              <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: 20, margin: 0, color: NAVY }}>{section.title}</h2>
              <div style={{ display: "grid", gap: 16, marginTop: 14 }}>
                {(section.fields || []).map((f) => (
                  <label key={f.id} style={labelStyle}>
                    <span>{f.label}{f.required ? " *" : ""}</span>
                    <Field f={f} value={answers[f.id]} onChange={set(f.id)} />
                    {f.help && <span style={{ fontSize: 11, textTransform: "none", letterSpacing: 0, color: "#64748b", fontFamily: "system-ui" }}>{f.help}</span>}
                  </label>
                ))}
              </div>
            </section>
          ))}

          {validationErrors.length > 0 && (
            <div data-testid="qf-validation-errors" role="alert"
                 style={{ background: "#fee2e2", color: "#991b1b", padding: 12, borderRadius: 4, fontSize: 13 }}>
              <b>Please fix the following:</b>
              <ul style={{ margin: "6px 0 0 18px" }}>
                {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" data-testid="qf-save-draft" onClick={saveDraft} disabled={savingDraft || submitting}
                    style={{ ...primaryBtn, background: "white", color: NAVY, border: `2px solid ${NAVY}` }}>
              {savingDraft ? "Saving…" : "Save draft"}
            </button>
            <button type="submit" data-testid="qf-submit" disabled={submitting}
                    style={primaryBtn}>
              {submitting ? "Submitting…" : "Submit questionnaire"}
            </button>
            {savedAt && <span data-testid="qf-saved-at" style={{ fontSize: 12, color: "#475569" }}>Draft saved · {new Date(savedAt).toLocaleTimeString()}</span>}
          </div>
          <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
            You can save your progress and return to this link. Once submitted, the link is retired for security.
          </p>
        </form>
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return <main data-testid="questionnaire-page" style={{ minHeight: "100vh", background: CREAM, color: NAVY }}>{children}</main>;
}
function Panel({ children }) {
  return <div style={{ maxWidth: 620, margin: "0 auto", padding: "80px 24px" }}>
    <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>{children}</div></div>;
}

const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 14, background: "white", color: "#0f172a", boxSizing: "border-box" };
const labelStyle = { display: "grid", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#475569", fontFamily: "'Space Mono', monospace" };
const h1Style = { fontFamily: "Playfair Display, serif", fontSize: 30, margin: "8px 0 4px", lineHeight: 1.1 };
const eyebrowStyle = { fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: GOLD, textTransform: "uppercase" };
const primaryBtn = { padding: "12px 22px", background: NAVY, color: CREAM, border: `2px solid ${GOLD}`, cursor: "pointer", fontSize: 13, letterSpacing: 1, textTransform: "uppercase", fontFamily: "'Space Mono', monospace" };
const linkStyle = { display: "inline-block", marginTop: 20, color: NAVY, textDecoration: "underline" };
